import { Vector3 } from 'three';
import { Vertex } from '@thirdPartyLibraries/three-mesh-halfedge/src';
import HalfEdgeMesh from '@/modules/three/common/halfEdgeMesh/HalfEdgeMesh.js';

/**
 * Closes an interproximal opening with CONCENTRIC RINGS: the arc is copied
 * inward several times, each copy scaled a little further toward the
 * opening's centroid, consecutive copies bridged with quads, and the
 * innermost one fanned to a centre vertex.
 *
 * This is the original, deliberately simple wall. It is a display surface:
 * the orthodontist sees a closed tooth, and the export replaces the whole
 * thing with a bridge built between the two neighbouring crowns once they
 * are in their final posed positions. So it does not have to tuck under the
 * crown, follow the medial or survive an offset — it only has to close,
 * look plausible and be cheap.
 *
 * Every vertex it creates is registered in `generated`, which is what lets
 * correction delete exactly this geometry again.
 *
 * NOTE: `createFace` RETURNS FALSE on refusal, it does not throw — the
 * try/catch this function used to carry never fired, and its face counter
 * counted refusals as successes.
 */
export function fillArcWithConcentricRings(
  mesh: HalfEdgeMesh,
  arc: Vertex[],
  ringCount: number = 3,
  generated?: Set<Vertex>,
): boolean {
  if (arc.length < 3) {
    console.warn(`fillArcWithConcentricRings: arc too short (${arc.length} vertices), need at least 3`);
    return false;
  }

  // Step 1: Low-pass a copy of the arc (open polyline, endpoints pinned).
  // The raw cut line zigzags at the few-vertex scale; a ring built from it
  // would carry every jag inward as a radial ridge across the whole wall.
  const smoothedArc = arc.map((v) => v.position.clone());
  const smoothBuffer = smoothedArc.map((p) => p.clone());
  for (let it = 0; it < 24; it++) {
    for (let i = 1; i < smoothedArc.length - 1; i++) {
      smoothBuffer[i]
        .copy(smoothedArc[i])
        .multiplyScalar(0.5)
        .addScaledVector(smoothedArc[i - 1], 0.25)
        .addScaledVector(smoothedArc[i + 1], 0.25);
    }
    for (let i = 1; i < smoothedArc.length - 1; i++) {
      smoothedArc[i].copy(smoothBuffer[i]);
    }
  }

  // Step 2: Centroid of the SMOOTHED arc (not the raw one)
  const centroid = new Vector3();
  for (const p of smoothedArc) {
    centroid.add(p);
  }
  centroid.divideScalar(smoothedArc.length);

  // Step 3: the rings themselves — copies of the smoothed arc marching
  // toward the centroid. The arc is treated as CLOSED (its two anchors are
  // joined by the wrap-around segment below), so each ring is a closed
  // polyline of the same length and the whole wall is a cone of quads.
  const rings: Vertex[][] = [];
  for (let k = 1; k <= ringCount; k++) {
    const t = k / (ringCount + 1);
    const ring = smoothedArc.map((position) => {
      const vertex = mesh.halfEdges.addVertex(new Vector3().lerpVectors(position, centroid, t));
      generated?.add(vertex);
      return vertex;
    });
    rings.push(ring);
  }
  const centerVertex = mesh.halfEdges.addVertex(centroid);
  generated?.add(centerVertex);

  let facesCreated = 0;
  const face = (a: Vertex, b: Vertex, c: Vertex): boolean => {
    if (mesh.halfEdges.createFace(a, b, c) === true) return true;
    return mesh.halfEdges.createFace(c, b, a) === true;
  };

  // Step 4: bridge every consecutive pair of rings, outer arc included, all
  // the way round (i → i + 1 modulo the length closes the anchor gap).
  const levels: Vertex[][] = [arc, ...rings];
  for (let level = 0; level + 1 < levels.length; level++) {
    const outer = levels[level];
    const inner = levels[level + 1];
    for (let i = 0; i < outer.length; i++) {
      const j = (i + 1) % outer.length;
      if (face(outer[i], outer[j], inner[j])) facesCreated++;
      if (face(outer[i], inner[j], inner[i])) facesCreated++;
    }
  }

  // Step 5: fan the innermost ring to the centre.
  const innermost = levels[levels.length - 1];
  for (let i = 0; i < innermost.length; i++) {
    const j = (i + 1) % innermost.length;
    if (face(innermost[i], innermost[j], centerVertex)) facesCreated++;
  }

  mesh.calculateBorderVertices();
  const expected = 2 * arc.length * ringCount + arc.length;
  console.log(
    `fillArcWithConcentricRings: arc ${arc.length} verts, ${ringCount} rings → ` +
      `${facesCreated}/${expected} faces, ${rings.length * arc.length + 1} new vertices`,
  );
  return facesCreated > 0;
}
